import { useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AgtechReleasePreset,
  SUPPORTED_AGTECH_RELEASE_COMMODITIES,
  type AgtechReleaseReportRequest,
  type SupportedAgtechReleaseCommodity,
} from "@/lib/api/cfr";

const SUPPORTED_COMMODITY_LABELS: Record<SupportedAgtechReleaseCommodity, string> = {
  C: "Corn",
  CNL: "Canola",
};

interface RangeEditorRow {
  ui_id: string;
  commodity: SupportedAgtechReleaseCommodity;
  from: string;
  to: string;
  preset_id: string | null;
}

export function newAgtechReleaseRange(): AgtechReleaseReportRequest["ranges"][number] {
  return {
    commodity: SUPPORTED_AGTECH_RELEASE_COMMODITIES[0],
    from: "",
    to: "",
  };
}

function newRangeRow(): RangeEditorRow {
  return {
    ui_id: uuidV4(),
    ...newAgtechReleaseRange(),
    preset_id: null,
  };
}

export function AgtechReleaseRangeBuilder({
  presets,
  onChange,
}: {
  presets: AgtechReleasePreset[];
  onChange: (ranges: AgtechReleaseReportRequest["ranges"]) => void;
}) {
  const [rangeRows, setRangeRows] = useState<RangeEditorRow[]>([newRangeRow()]);

  const updateRows = (rows: RangeEditorRow[]) => {
    setRangeRows(rows);
    onChange(rows.map((row: RangeEditorRow) => ({
      commodity: row.commodity,
      from: row.from,
      to: row.to,
    })));
  };

  const updateRangeRow = (ui_id: string, patch: Partial<RangeEditorRow>) => {
    updateRows(rangeRows.map((row: RangeEditorRow) => row.ui_id === ui_id ? { ...row, ...patch } : row));
  };

  const handlePresetChange = (ui_id: string, preset_id: string) => {
    const preset = presets.find((item: AgtechReleasePreset) => item.id === preset_id);
    if (!preset) return;
    updateRangeRow(ui_id, {
      commodity: preset.commodity,
      from: preset.from,
      to: preset.to,
      preset_id: preset.id,
    });
  };

  const removeRangeRow = (ui_id: string) => {
    const remaining = rangeRows.filter((row: RangeEditorRow) => row.ui_id !== ui_id);
    updateRows(remaining.length > 0 ? remaining : [newRangeRow()]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Report Ranges</Label>
        <Button variant="outline" size="sm" onClick={() => updateRows([...rangeRows, newRangeRow()])}>
          <Plus className="h-4 w-4 mr-1" /> Add range
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        {rangeRows.map((row: RangeEditorRow) => {
          const rowPresets = presets.filter((preset: AgtechReleasePreset) => preset.commodity === row.commodity);
          return (
            <div key={row.ui_id} className="flex flex-wrap items-end gap-2">
              <div className="grid gap-1">
                <Label className="text-xs">Commodity</Label>
                <Select
                  value={row.commodity}
                  onValueChange={(value: string) => updateRangeRow(row.ui_id, {
                    commodity: value as SupportedAgtechReleaseCommodity,
                    preset_id: null,
                  })}
                >
                  <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_AGTECH_RELEASE_COMMODITIES.map((commodity: SupportedAgtechReleaseCommodity) => (
                      <SelectItem key={commodity} value={commodity}>{SUPPORTED_COMMODITY_LABELS[commodity]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1">
                <Label className="text-xs">Preset</Label>
                <Select
                  value={row.preset_id ?? ""}
                  onValueChange={(value: string) => handlePresetChange(row.ui_id, value)}
                  disabled={rowPresets.length === 0}
                >
                  <SelectTrigger className="w-[160px]"><SelectValue placeholder="Select preset" /></SelectTrigger>
                  <SelectContent>
                    {rowPresets.map((preset: AgtechReleasePreset) => (
                      <SelectItem key={preset.id} value={preset.id}>{preset.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  className="w-[150px]"
                  value={row.from}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateRangeRow(row.ui_id, { from: event.target.value, preset_id: null })
                  }
                />
              </div>

              <div className="grid gap-1">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  className="w-[150px]"
                  value={row.to}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateRangeRow(row.ui_id, { to: event.target.value, preset_id: null })
                  }
                />
              </div>

              <Button variant="ghost" size="icon" onClick={() => removeRangeRow(row.ui_id)} aria-label="Remove range">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
